
import { Star } from 'lucide-react';
import { Card, CardContent } from "./ui/card";
import { Avatar, AvatarImage, AvatarFallback } from "./ui/avatar";

interface TestimonialProps {
  quote: string;
  author: string;
  role: string;
  stars: number;
  imageSrc: string;
}

const TestimonialCard = ({ quote, author, role, stars, imageSrc }: TestimonialProps) => {
  // Get the first letter of first and last name for the avatar fallback
  const nameParts = author.split(' ');
  const initials = nameParts.length > 1 
    ? `${nameParts[0][0]}${nameParts[1][0]}` 
    : nameParts[0][0];

  return (
    <Card className="bg-white text-book-darkBlue p-8 rounded-lg border border-book-yellow/30 shadow-xl h-full">
      <CardContent className="p-0 flex flex-col h-full">
        <div className="flex mb-3">
          {Array.from({ length: stars }).map((_, i) => (
            <Star key={i} className="w-5 h-5 fill-book-gold text-book-gold" fill="#D9C963" />
          ))}
        </div>
        <p className="text-gray-700 mb-6 text-base md:text-lg flex-grow">{quote}</p>
        <div className="flex items-center pt-4 border-t border-gray-100">
          <Avatar className="w-12 h-12 rounded-full overflow-hidden mr-3 border border-book-yellow/20">
            <AvatarImage src={imageSrc} alt={author} className="object-cover" />
            <AvatarFallback className="bg-book-yellow/20 text-book-darkBlue">{initials}</AvatarFallback>
          </Avatar>
          <div>
            <p className="font-bold text-sm">{author}</p>
            <p className="text-gray-500 text-xs">{role}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const TestimonialSection = () => {
  const testimonials = [
    {
      quote: "Since adopting Rey's system, I've experienced no downtime in three years. Now, I'm focused on delegation and hiring employees to handle additional projects. Rey's mentorship has transformed my professional journey, allowing me to achieve the work-life balance I've always dreamed of.",
      author: "Quinn Thomas",
      role: "Independent Oracle Cloud Consultant",
      stars: 5,
      imageSrc: "/lovable-uploads/18e1c469-be0a-4116-b51d-468fadc121b2.png"
    },
    {
      quote: "Rey mentored me through the process of becoming an independent Oracle Cloud consultant, providing invaluable guidance at every step. From understanding how to find clients and build referral networks to planning the logistics of business structure, payroll, and employee management, Rey shared his expertise generously. His advice gave me the confidence to navigate the transition and set up my own business with a clear growth plan.",
      author: "Christian Wendlandt",
      role: "Independent Oracle Cloud Consultant",
      stars: 5,
      imageSrc: "/lovable-uploads/6cb68c22-af7c-467a-8c4b-f28ebe3ab3f3.png"
    },
    {
      quote: "Rey and I have been working together for over a decade. He has always paved the way for my switch to independent consulting and has provided countless hours of tips, tricks and recommendations. If only I had a book that takes all of Rey's useful knowledge and puts into succinct guide-book, I might have started my journey of independence even earlier.",
      author: "Anton Rzhanov",
      role: "Independent Oracle Cloud HCM Consultant",
      stars: 5,
      imageSrc: "/lovable-uploads/32430f0f-d1c1-4007-aa37-3f6113d74f76.png"
    }
  ];

  return (
    <section className="light-section bg-gray-50 py-24">
      <div className="container mx-auto px-4">
        <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-book-darkBlue">
          Success Stories From <br className="hidden md:block" />
          Independent Consultants
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {testimonials.map((testimonial, index) => (
            <TestimonialCard
              key={index}
              quote={testimonial.quote}
              author={testimonial.author}
              role={testimonial.role}
              stars={testimonial.stars}
              imageSrc={testimonial.imageSrc}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default TestimonialSection;
